import { EMailTemplateType, INotificationTemplateDto } from "@kuika/kuika-cl-model";
import freeMailTemplatesJson from "../../../json/freeMailTemplates.json";
import { DESIGNER_LANGUAGE_OPTIONS, IDesignerLanguageOption } from "../../../shared/utility/designer-languages";
import { KMainFunctions } from "../../../shared/utility/main-functions";

/**
 * The free Unlayer starter catalog and the blank design a "Blank Email" starts from.
 *
 * The catalog ships as `designer/src/json/freeMailTemplates.json` — 422 four-element
 * tuples, `[id, displayName, unlayerSlug, previewUrl]`. Selecting one materialises it
 * through `POST /NotificationTemplate/addTemplateByTemplateID`, which copies the row the
 * backend already holds for that id; the slug is only needed when the catalog itself is
 * regenerated (see the procedure below).
 */

export interface IEmailTemplateCatalogItem {
  id: string;
  name: string;
  /** Unlayer's own slug. Unused at runtime — kept for catalog regeneration. */
  slug: string;
  thumbnailUrl: string;
  /** Browsing category, derived from the name. Undefined when nothing matched. */
  category?: string;
}

interface IEmailTemplateCategoryRule {
  category: string;
  pattern: RegExp;
}

/**
 * The catalog ships four columns — id, name, slug, thumbnail — and no category of any
 * kind, so 422 cards arrive as one undifferentiated wall. These rules read the category
 * back out of the name.
 *
 * Derived rather than stored on purpose: the catalog is regenerated from Unlayer (which
 * is why `slug` is kept), and a fifth column would be dropped by the next regeneration
 * while new templates would arrive uncategorised. This way they classify themselves.
 *
 * ORDER IS THE RULE — first match wins. Seasonal comes first because "Christmas Sale"
 * is a holiday template that happens to mention a sale, and `Business & Services` comes
 * last because its vocabulary is broad enough to swallow anything left.
 *
 * Coverage over the shipped catalog is 422/426 names; the four that match nothing carry
 * no category and are reachable through "All" and search, which is the honest outcome —
 * better than a bucket named after the fact that we could not tell.
 */
const CATEGORY_RULES: IEmailTemplateCategoryRule[] = [
  {
    category: "Holidays & Seasonal",
    pattern:
      /christmas|xmas|easter|halloween|valentine|thanksgiving|new ?year|holiday|black friday|cyber monday|mother|father|presidents|women|veteran|independence|labor day|memorial day|summer|winter|spring|autumn|st\.? ?patrick|hanukkah|diwali|ramadan|eid|carnival|oktoberfest|boxing day|earth day|april fool|season|anniversary|birthday|congrat|celebrat|party|wedding|baby shower|graduation|cinco de mayo|martin luther|super bowl/
  },
  {
    category: "Customer Relations",
    pattern:
      /apolog|sorry|inconvenience|outage|downtime|incident|correction|oops|feedback|review|survey|rating|testimonial|nps|thanks|thank you|follow ?up/
  },
  {
    category: "Orders & Shipping",
    pattern:
      /order|shipping|delivery|invoice|receipt|purchase|payment|refund|cart|checkout|tracking|billing|renewal|subscription|money|trial ended/
  },
  {
    // `confirm` on its own used to live here and was far too greedy: "Event
    // Confirmation" and "Hotel Booking Confirmation" are not account emails. Only the
    // account-shaped phrasings match now; the rest fall through to a truer rule.
    category: "Account & Security",
    pattern:
      /password|verification|verify|sign ?up|signup|welcome|activation|activate|login|log ?in|two.factor|otp|security|account|onboard|registration|register|confirm your|confirmation code/
  },
  {
    category: "Promotions & Offers",
    pattern:
      /offer|sale|discount|deal|promo|coupon|voucher|% ?off|clearance|flash|giveaway|contest|reward|loyalty|referral|gift|best collection/
  },
  {
    category: "Events & Invitations",
    pattern:
      /event|invitation|invite|webinar|conference|meetup|rsvp|ticket|appointment|reminder|schedule|booking|reservation|hackathon|trade show|quiz|a night to remember/
  },
  {
    category: "Newsletter & Announcements",
    pattern:
      /newsletter|announce|update|news|digest|release|changelog|blog|launch|introduc|press|top stories|trending|our list|one last email/
  },
  {
    category: "Education & Nonprofit",
    pattern:
      /school|course|education|learn|university|student|training|charity|donation|donate|nonprofit|fundrais|book|library|workshop|scholarship|awareness|support |crisis|generosity|green planet|water|mentor|personal development|resume|curriculum vitae/
  },
  {
    category: "Health & Wellness",
    pattern:
      /health|fitness|gym|medical|doctor|clinic|dental|pharmac|wellness|yoga|hospital|care|therapy|nutrition|diet|spa|massage|exercise|trainer|sports|cancer|diabetes/
  },
  {
    category: "Food & Travel",
    pattern:
      /restaurant|food|pizza|coffee|cafe|bakery|menu|recipe|drink|beverage|burger|wine|beer|grocery|catering|chocolate|juice|travel|hotel|flight|trip|tour|resort|vacation|airline|cruise|camping|adventure/
  },
  {
    category: "Retail & Fashion",
    pattern:
      /fashion|clothing|shoe|apparel|jewel|beauty|cosmetic|store|shop|product|boutique|sneaker|watch|bag|toy|makeup|gadget|camera|game/
  },
  {
    category: "Business & Services",
    pattern:
      /business|corporate|agency|marketing|brand|consult|startup|saas|software|tech|finance|bank|insurance|legal|hr\b|interview|recruit|job|hiring|team|colleague|client|customer|partner|report|proposal|elevator pitch|it |realtor|real estate|property|apartment|house|rent|mortgage|\bcar\b|auto|vehicle|dealership|interior|furniture|construct|architect|home|agriculture|plumbing|moving|photograph|content creator|remote working|weather|company|service/
  }
];

/**
 * `toLowerCase` and not `toLocaleLowerCase`: the rules are ASCII and the result must not
 * depend on the developer's or the deployment's locale.
 */
export const categorizeEmailTemplate = (name: string): string | undefined =>
  CATEGORY_RULES.find((rule: IEmailTemplateCategoryRule): boolean => rule.pattern.test((name ?? "").toLowerCase()))
    ?.category;

/** Categories present in a set, largest first, each with how many carry it. */
export const emailTemplateCategories = (
  items: IEmailTemplateCatalogItem[]
): { label: string; value: string; count: number }[] => {
  const counts: Map<string, number> = new Map();
  items.forEach((item: IEmailTemplateCatalogItem): void => {
    if (!item.category) return;
    counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a: [string, number], b: [string, number]) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, count]: [string, number]) => ({ label, value: label, count }));
};

/**
 * Silmeyiniz!
 * Tekrar template güncellemesi gerekirse (freeMailTemplates.json'ın yeniden üretimi):
 * Adım 1 https://unlayer.com/templates browser ile açılır
 * Adım 2 Free templateler seçilir ve popularity'ye göre sıralanır.
 * Adım 3 f12 ile console'a aşağıdaki kod yazılır ve tüm templateler listelenir.
 * addEventListener('scroll', (event) => {  document.getElementsByClassName("load-btn") !== undefined &&
 * document.getElementsByClassName("load-btn").length > 0 && document.getElementsByClassName("load-btn")[0].click()  });
 * Adım 4 f12 ile console'a aşağıdaki kod yazılır ve tüm templateler bir json'a çevirilir.
 * let array = [];
 * for (let node of document.querySelector("#browser > div > div > div.sc-fznWOq.cqDPIl > div.gallery").childNodes) {
 * let img = node.querySelector("div > div > div > img")
 *     array.push({title: img.alt,  src: img.src.substr(43).substr(0,img.src.substr(43).indexOf("/"))  } );
 * }
 * JSON.stringify(array);
 * Adım 5 http://json2table.com/ ile excel'e aktarılır.
 * Adım 6 Excel'de calculated bir alan açılır ve 50 karakteri aşan alanların isimleri kısaltılır,
 * customizationlar gerekiyorsa yapılır
 * Adım 7 aşağıdaki calculated alan ile excel'den sql'e kontrollü bir şekilde atılır.
 * "INSERT INTO [dbo].[KNotificationTemplate] ([Id] ,[Name] ,[Body] ,[Design] ,[TemplateType] ,[Parameters] ,
 * [KProject_Id] ,[CreatedAt] ,[CreatedUser_Id] ,[IsDeleted] ,[KDomain_Id]) VALUES"&" ( '" & A1 & "' ,'"&B1&"' ,
 * NULL , NULL,0 ,NULL ,'87E315AF-D589-46B6-7BC3-08DA7AA4D4A5' ,GETDATE() ,
 * '01D325F8-E84B-496E-8D09-0A4A5626234E' ,0,'DDDD7FB9-1AE8-49EC-BF42-2376EE9EDE61')"
 * Adım 8 https://www.convertcsv.com/csv-to-json.htm CSV to Json Array ile tuple formatına çevrilir; her tuple
 * `[id, name, slug, previewUrl]` olmalıdır. `NotificationTemplateService.GetNotificationTemplateFromUnlayer(slug)`
 * ile html/design çekilip `Update` ile yazılırsa beta ortamdaki unlayer template projesi de güncellenmiş olur.
 */
const RAW_CATALOG: string[][] = freeMailTemplatesJson as unknown as string[][];

/** Tuple rows → typed items. Rows missing an id, a name or a preview are dropped. */
export const toEmailTemplates = (raw: string[][]): IEmailTemplateCatalogItem[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row: string[]): boolean => Array.isArray(row) && !!row[0] && !!row[1] && !!row[3])
    .map((row: string[]): IEmailTemplateCatalogItem => ({
      id: row[0],
      name: row[1],
      slug: row[2] ?? "",
      thumbnailUrl: row[3],
      category: categorizeEmailTemplate(row[1])
    }));
};

export const EMAIL_TEMPLATE_CATALOG: IEmailTemplateCatalogItem[] = toEmailTemplates(RAW_CATALOG);

/**
 * Same matching the Emails list has always used: the query is transliterated, the
 * candidate name is not. Kept verbatim so which templates a query surfaces does not
 * shift under the refactor.
 */
export const filterEmailTemplates = (
  items: IEmailTemplateCatalogItem[],
  searchText: string
): IEmailTemplateCatalogItem[] => {
  const needle: string = KMainFunctions.turkishCharsToEnglish(searchText ?? "").toLowerCase();
  return items.filter((item: IEmailTemplateCatalogItem): boolean => item.name.toLowerCase().indexOf(needle) !== -1);
};

/**
 * The design a blank email starts from. The `_meta` html ids and the row/column ids are
 * what Unlayer keys its blocks off, so this object is reproduced verbatim from the
 * pre-refactor `create()` — do not "tidy" the ids.
 */
export const BLANK_EMAIL_DESIGN: string = JSON.stringify({
  counters: { u_column: 1, u_row: 1 },
  body: {
    id: "rXl8JgXH1J",
    rows: [
      {
        id: "rNPmn3h2Kp",
        cells: [1],
        columns: [
          {
            id: "IQp9GQrtjN",
            contents: [],
            values: {
              backgroundColor: "",
              padding: "0px",
              border: {},
              _meta: {
                htmlID: "u_column_1",
                htmlClassNames: "u_column"
              }
            }
          }
        ],
        values: {
          displayCondition: null,
          columns: false,
          backgroundColor: "",
          columnsBackgroundColor: "",
          backgroundImage: {
            url: "",
            fullWidth: true,
            repeat: false,
            center: true,
            cover: false
          },
          padding: "0px",
          anchor: "",
          hideDesktop: false,
          _meta: { htmlID: "u_row_1", htmlClassNames: "u_row" },
          selectable: true,
          draggable: true,
          duplicatable: true,
          deletable: true,
          hideable: true
        }
      }
    ],
    values: {
      textColor: "#000000",
      backgroundColor: "#e7e7e7",
      backgroundImage: {
        url: "",
        fullWidth: true,
        repeat: false,
        center: true,
        cover: false
      },
      contentWidth: "500px",
      contentAlign: "center",
      fontFamily: {
        label: "Arial",
        value: "arial,helvetica,sans-serif"
      },
      preheaderText: "",
      linkStyle: {
        body: true,
        linkColor: "#0000ee",
        linkHoverColor: "#0000ee",
        linkUnderline: true,
        linkHoverUnderline: true
      },
      _meta: { htmlID: "u_body", htmlClassNames: "u_body" }
    }
  },
  schemaVersion: 7
});

/**
 * The locales the multi-language switcher offers — the designer's shared catalog, so the
 * Email Builder and the Languages panel always agree (flags included).
 */
export type IEmailLanguageOption = IDesignerLanguageOption;

export const EMAIL_LANGUAGE_OPTIONS: IEmailLanguageOption[] = DESIGNER_LANGUAGE_OPTIONS;

/**
 * The order the Emails list has always used: the system templates a generated app sends
 * by name first, then everything else alphabetically. A total order, unlike the panel's
 * original comparator, which returned -1 for both sides when they were both system
 * templates and so depended on the input order.
 */
export const sortEmailTemplates = (templates: INotificationTemplateDto[]): INotificationTemplateDto[] =>
  [...templates].sort((a: INotificationTemplateDto, b: INotificationTemplateDto): number => {
    const aIsSystem: boolean = a.templateStatus !== EMailTemplateType.Default;
    const bIsSystem: boolean = b.templateStatus !== EMailTemplateType.Default;
    if (aIsSystem !== bIsSystem) return aIsSystem ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
